import { EditOutlined, LockOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  ConfigProvider,
  Divider,
  Drawer,
  Form,
  Row,
  Space,
  Tag,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearSettingsResult,
  loadSettingsStatus,
} from "../../store/user/general/userActions";
import ChangePassword from "./ChangePassword";
import { MfaEditDrawer } from "./MfaEditDrawer";
import UpdatePhoneNumber from "./UpdatePhoneNumber";
import PreferencesPanel from "./PreferencesPanel";

const { Text, Title } = Typography;

const PanelCard = ({ children }) => (
  <div
    style={{
      background: "#fff",
      padding: 24,
      borderRadius: 8,
      border: "1px solid #f0f0f0",
      marginBottom: 32,
      flex: 1,
      boxSizing: "border-box",
    }}
  >
    {children}
  </div>
);

export default function Settings() {
  //
  const dispatch = useDispatch();

  //
  const status = useSelector((state) => state.user.general.settings.status);
  const loading = useSelector((state) => state.user.general.settings.loading);
  const loadingError = useSelector(
    (state) => state.user.general.settings.loadingError
  );
  const fullName = useSelector((state) => state.actor.details.data.FullName);
  const displayEmail = useSelector(
    (state) => state.actor.details.data.DisplayEmail
  );
  const phone = status?.phoneNumber || "";
  const phoneConfirmed = status?.phoneNumberConfirmed ?? false;

  //
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [phoneNumberOpen, setPhoneNumberOpen] = useState(false);
  const [mfaDrawerOpen, setMfaDrawerOpen] = useState(false);

  //
  useEffect(() => {
    dispatch(loadSettingsStatus());
    dispatch(clearSettingsResult());
  }, [dispatch]);

  return (
    <ConfigProvider
      componentSize="small"
      theme={{
        token: {
          colorPrimary: "#78a1cf",
          borderRadius: 8,
        },
        components: {
          Button: {
            colorPrimaryHover: "#a2bfdb",
            colorPrimaryActive: "#597ba8",
          },
        },
      }}
    >
      <Form layout="vertical">
        {loadingError === true && (
          <Alert
            type="error"
            message="Something went wrong while loading your user profile."
            showIcon
          />
        )}

        <div style={{ width: "100%" }}>
          <Row
            gutter={{ xs: 0, sm: 0, md: 32 }}
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Col
              xs={24}
              sm={24}
              md={6}
              lg={6}
              xl={6}
              xxl={6}
              style={{ display: "flex" }}
            >
              <PanelCard>
                <Title level={5} style={{ marginBottom: 0 }}>
                  Profile
                </Title>
                <Divider style={{ marginTop: 0 }} />

                <Form.Item label={<Text strong>Full Name</Text>}>
                  <Text>{fullName ?? "—"}</Text>
                </Form.Item>

                <Form.Item label={<Text strong>Email</Text>}>
                  <Text>{displayEmail ?? "—"}</Text>
                </Form.Item>

                <Form.Item
                  label={
                    <Space size={8}>
                      <Text strong>Phone Number</Text>
                      {phoneConfirmed && (
                        <Tag color={phoneConfirmed ? "success" : "default"}>
                          {phoneConfirmed ? "Verified" : "Unverified"}
                        </Tag>
                      )}
                    </Space>
                  }
                >
                  <Space>
                    <Text>
                      {phone || <span style={{ color: "#999" }}>Not set</span>}
                    </Text>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => setPhoneNumberOpen(true)}
                      disabled={loading}
                    >
                      Edit
                    </Button>
                  </Space>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="link"
                    icon={<LockOutlined />}
                    onClick={() => setChangePwdOpen(true)}
                    style={{ paddingLeft: 0 }}
                  >
                    Change password
                  </Button>
                </Form.Item>

                <Title level={5} style={{ marginBottom: 0 }}>
                  Multi-Factor Authentication
                </Title>
                <Divider style={{ marginTop: 0 }} />

                <Form.Item label={<Text strong>Verification method</Text>}>
                  <Space>
                    <Text>{status?.preferredProvider || "Email"}</Text>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => setMfaDrawerOpen(true)}
                      disabled={loading}
                    >
                      Edit
                    </Button>
                  </Space>
                </Form.Item>
              </PanelCard>
            </Col>
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={12}
              xxl={12}
              style={{ display: "flex" }}
            >
              <PanelCard>
                <PreferencesPanel />
              </PanelCard>
            </Col>
          </Row>
        </div>

        <Drawer
          open={changePwdOpen}
          onClose={() => setChangePwdOpen(false)}
          width={440}
          title="Change Password"
          destroyOnClose
        >
          <ChangePassword onCloseHandler={() => setChangePwdOpen(false)} />
        </Drawer>

        <Drawer
          open={phoneNumberOpen}
          onClose={() => setPhoneNumberOpen(false)}
          width={350}
          title="Update Phone Number"
          destroyOnClose
        >
          <UpdatePhoneNumber onCloseHandler={() => setPhoneNumberOpen(false)} />
        </Drawer>

        <Drawer
          open={mfaDrawerOpen}
          onClose={() => setMfaDrawerOpen(false)}
          width={350}
          title="Edit Multi-Factor Authentication"
          destroyOnClose
        >
          <MfaEditDrawer />
        </Drawer>
      </Form>
    </ConfigProvider>
  );
}
